import { Box, Tab, Tabs, Typography } from "@mui/material";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { auth } from "../config/firebase";
import { PropertyDocumentList } from "../features/documents/components/PropertyDocumentList";
import { PropertyPageHeader } from "../features/properties/components/PropertyPageHeader";
import { PropertyDetailsTab } from "../features/properties/components/PropertyDetailsTab";
import { PropertyReportTabs } from "../features/properties/components/PropertyReportTabs";
import { PropertyAgentChatTab } from "../features/properties/components/PropertyAgentChatTab";
import { selectPropertyById } from "../features/properties/store/propertiesSelectors";
import { RootState } from "../store/rootReducer";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  flex?: number;
  display?: string;
  padContainer?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { 
    children, 
    value, 
    index, 
    flex,
    display,
    padContainer = true,
    ...other 
  } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`property-tabpanel-${index}`}
      aria-labelledby={`property-tab-${index}`}
      {...other}
      sx={{ 
        flex: flex || 1,
        display: value === index ? display || "flex" : "none",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {value === index && (
        <Box sx={{ 
          display: "flex",
          flexDirection: "column",
          flex: 1,
          pt: padContainer ? 3 : 0,
          minHeight: 0,
          overflow: "hidden",
        }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `property-tab-${index}`,
    "aria-controls": `property-tabpanel-${index}`,
  };
}

export const PropertyDetailsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { id } = useParams<{ id: string }>();
  const property = useSelector((state: RootState) =>
    selectPropertyById(state, id || "")
  );

  if (!property) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error">Property not found</Typography>
      </Box>
    );
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const tabs = [
    "Chat",
    "Property Report", 
    "Property Details",
    "Documents",
  ];

  return (
    <Box sx={{ 
      p: 0, 
      minHeight: "100vh", 
      maxHeight: "100vh", 
      display: "flex", 
      flexDirection: "column",
      overflow: "hidden"
    }}>
      <Box sx={{ p: 2, flexShrink: 0 }}>
        <PropertyPageHeader property={property} />
      </Box>
      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="property details tabs"
            data-testid="property-details-tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={tab}
                {...a11yProps(index)}
                data-testid={`${tab.toLowerCase().replace(/\s+/g, "-")}-tab`}
              />
            ))}
          </Tabs>
        </Box>

        <TabPanel 
          value={activeTab} 
          index={0}
          flex={1}
          display="flex"
          padContainer={false}
        >
          <PropertyAgentChatTab 
            property={property} 
            userId={auth.currentUser?.uid || ""} 
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <PropertyReportTabs
            userId={auth.currentUser?.uid || ""}
            propertyId={id || ""}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <PropertyDetailsTab property={property} />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <PropertyDocumentList property={property} />
        </TabPanel>
      </Box>
    </Box>
  );
};
